import { cn } from '@/lib/utils';

/**
 * Gráfico de barras agrupadas "año de un vistazo" (12 meses).
 *
 * CSS puro (sin recharts) — barras verticales escaladas al máximo global.
 * Cada mes puede tener 1 o 2 series (ej: ingresos vs egresos, débito vs
 * crédito). Valores no-negativos. El mes destacado (`highlightMonth`) se
 * resalta con un anillo sutil.
 *
 * No es un competidor de una librería de charts — es una visualización
 * liviana, on-brand (tokens del DS) y suficiente para el resumen anual.
 */

const MONTH_SHORT = ['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export interface BarSeries {
  /** Clave del valor dentro de cada punto mensual. */
  key: string;
  label: string;
  /** Clase Tailwind de color de la barra (ej: 'bg-primary'). */
  colorClass: string;
}

export interface MonthlyPoint {
  month: number; // 1..12
  values: Record<string, number>;
}

interface MonthlyBarChartProps {
  data: MonthlyPoint[];
  series: BarSeries[];
  formatValue: (n: number) => string;
  /** Mes (1..12) a resaltar — típicamente el mes actual. */
  highlightMonth?: number;
  className?: string;
}

export function MonthlyBarChart({
  data,
  series,
  formatValue,
  highlightMonth,
  className,
}: MonthlyBarChartProps) {
  // Coerce a número (Postgres a veces serializa numerics como string).
  const v = (val: unknown): number => {
    const n = typeof val === 'number' ? val : Number(val ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const max = Math.max(
    1,
    ...data.flatMap((d) => series.map((s) => v(d.values[s.key])))
  );
  const hasAnyValue = data.some((d) => series.some((s) => v(d.values[s.key]) > 0));

  // Si hay un outlier MUCHO más grande que los demás (ej: 1 mes con 1B y los
  // demás con 10M), capear al p90 para que las barras "normales" sean visibles.
  // El outlier muestra como barra al 100% (clipped); las demás escalan al p90.
  const sortedValues = data
    .flatMap((d) => series.map((s) => v(d.values[s.key])))
    .filter((x) => x > 0)
    .sort((a, b) => a - b);
  const p90 = sortedValues.length > 0 ? sortedValues[Math.floor(sortedValues.length * 0.9)]! : max;
  const scaleMax = Math.max(1, p90, max * 0.3); // nunca por debajo del 30% del max real

  return (
    <div className={cn('w-full', className)}>
      <div className="relative flex h-56 items-end gap-1.5">
        {!hasAnyValue && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            Sin movimientos en el período
          </div>
        )}
        {data.map((point) => {
          const isHighlight = point.month === highlightMonth;
          const tooltip = series
            .map((s) => `${s.label}: ${formatValue(v(point.values[s.key]))}`)
            .join('\n');
          // Total del mes (suma de series) para etiqueta debajo del label
          const monthTotal = series.reduce((a, s) => a + v(point.values[s.key]), 0);
          return (
            <div
              key={point.month}
              className={cn(
                'group flex h-full flex-1 flex-col items-center justify-end gap-1 rounded-md pb-1 pt-2 transition-colors',
                isHighlight ? 'bg-muted' : 'hover:bg-muted/60'
              )}
              title={`${MONTH_SHORT[point.month - 1]} — \n${tooltip}`}
            >
              <div className="flex w-full flex-1 items-end justify-center gap-0.5">
                {series.map((s) => {
                  const val = v(point.values[s.key]);
                  const pct = Math.min(100, (val / scaleMax) * 100);
                  return (
                    <div
                      key={s.key}
                      className={cn(
                        'w-full max-w-[18px] rounded-t-sm transition-all',
                        s.colorClass,
                        val === 0 && 'opacity-30'
                      )}
                      style={{
                        height: `${Math.max(val === 0 ? 2 : 8, pct)}%`,
                        minHeight: val > 0 ? '12px' : '3px',
                      }}
                    />
                  );
                })}
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium uppercase',
                  isHighlight ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {MONTH_SHORT[point.month - 1]}
              </span>
              {monthTotal > 0 && (
                <span className="text-[9px] tabular-nums leading-none text-muted-foreground">
                  {formatValue(monthTotal).replace(/Gs /, '')}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={cn('inline-block size-2.5 rounded-sm', s.colorClass)} />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}
